export interface ValueScale {
  min: number;
  max: number;
  ticks: number[];
  /** A value's distance from the plot's bottom edge, in px. */
  toY: (value: number) => number;
}

function niceStep(span: number, sections: number): number {
  const raw = span / sections;
  const power = Math.pow(10, Math.floor(Math.log10(raw)));
  const fraction = raw / power;
  const nice =
    fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : 5;
  return nice === 5 && fraction > 5 ? 10 * power : nice * power;
}

/**
 * A value axis over `values` with round ticks. Zero is always on it (bars
 * and areas grow from it); `min` / `max` given by the caller win.
 */
export function valueScale(
  values: number[],
  plotHeight: number,
  fixed: { min?: number; max?: number },
  sections = 4,
): ValueScale {
  let low = Math.min(0, ...values);
  let high = Math.max(0, ...values);
  if (fixed.min !== undefined) low = fixed.min;
  if (fixed.max !== undefined) high = fixed.max;
  if (!(high > low)) high = low + 1;
  const step = niceStep(high - low, sections);
  const min = fixed.min !== undefined ? low : Math.floor(low / step) * step;
  const max = fixed.max !== undefined ? high : Math.ceil(high / step) * step;
  const ticks: number[] = [];
  const first = Math.ceil(min / step - 1e-9) * step;
  for (let tick = first; tick <= max + step * 1e-9; tick += step)
    ticks.push(Math.round(tick / step) * step || 0);
  // A fixed end is a tick of its own; a round tick too close to it would
  // print its label over the end's.
  if (fixed.min !== undefined && ticks[0] !== min) {
    if (ticks[0] - min < step * 0.3) ticks.shift();
    ticks.unshift(min);
  }
  if (fixed.max !== undefined && ticks[ticks.length - 1] !== max) {
    if (max - ticks[ticks.length - 1] < step * 0.3) ticks.pop();
    ticks.push(max);
  }
  const span = max - min;
  return {
    min,
    max,
    ticks,
    toY: value =>
      (Math.min(Math.max(value, min), max) - min) * (plotHeight / span),
  };
}
